import React from 'react';

const colors = require('tailwindcss/colors');

interface InputToolProps {
  disabled?: boolean;
  submitCallback: (code: string[]) => void;
}

interface InputToolState {
  code: string[];
  selected: number;
}

export class InputTool extends React.Component<InputToolProps, InputToolState> {
  private colorPalette = ['Red', 'Blue', 'Yellow', 'Green', 'Black', 'White'];

  constructor(props: InputToolProps){
    super(props);
    this.selectNumber = this.selectNumber.bind(this);
    this.state = {code: ['Gray', 'Gray', 'Gray', 'Gray'], selected: 0};
  }

  generateColorButtons(): React.ReactNode[]{
    const buttons: React.ReactNode[] = [];
    for(let i = 0; i < this.colorPalette.length; i++){
      buttons.push( <button
                      key={i}
                      className='h-full max-h-[90px] rounded-lg aspect-square apect-square'
                      style={{backgroundColor: this.colorPalette[i]}}
                      onClick = {() => this.setColorAtSelected(this.colorPalette[i], true, true)}
                      disabled = {this.props.disabled}
                    ></button>)
    }
    return buttons;
  }

  selectNumber(x: number){
    this.setState( (prevState) => {
      return {
        code: prevState.code,
        selected: x,
      }
    })
  }

  clear(): void{
    this.setState( (prevState) => {
      return {
        code: ['Gray', 'Gray', 'Gray', 'Gray'],
        selected: 0
      }
    })
  }

  setColorAtSelected(color: string, wrap: boolean = true, incrementAfterSelection: boolean = true){
    this.setState( (prevState) => {
      const newCode: string[] = [...prevState.code];
      newCode[prevState.selected] = color;
      let nextSelection = incrementAfterSelection ? prevState.selected + 1 : prevState.selected;
      if(nextSelection >= 4){
        nextSelection = wrap ? 0 : 3;
      }
      return {
        code: newCode,
        selected: nextSelection
      }
    })
  }

  submitCode(): void{
    this.props.submitCallback(this.state.code);
    this.setState( (prevState) => {
      return {
        code: ['Gray', 'Gray', 'Gray', 'Gray'],
        selected: 0
      }
    })
  }

  allColorsNonGray(): boolean{
    const set: Set<string> = new Set(this.state.code);
    return !set.has('Gray');
  }

  allColorsUnique(): boolean{
    const set: Set<string> = new Set(this.state.code);
    return set.size === 4;
  }

  validateCode(): boolean{
    return this.allColorsNonGray() && this.allColorsUnique();
  }

  generateSelectableNodes(): React.ReactNode[]{
    const {code, selected} = this.state;
    const nodes: React.ReactNode[] = [];
    for(let i = 0; i < code.length; i++){
      nodes.push(<SelectableInputNode key={i} color={code[i]} selected={selected===i} index={i} callBack={this.selectNumber} disabled={this.props.disabled}/>)
    }
    return nodes;
  }

  render(): React.ReactNode {
    const { disabled } = this.props;
    return (
      <div className='flex flex-col items-center justify-center w-full h-full gap-1 pt-4'>
        {
        /* Rendered Selection 
        *  Can click on place to highlight it as target of editing
        *  Editing a color then shifts the editing target by 1 right (Wraps around?)
        */
        }

        <div className='flex flex-row justify-center items-center h-[25%] w-[60%] mb-4'>
          <div className='flex flex-row justify-center h-full gap-1 aspect-square'>
            {this.generateSelectableNodes()}
          </div>
        </div>

        {/* Selection Tool (Clickable Button to change target of editings color)*/}
        <div className='flex flex-row justify-center items-center w-4/5 md:w-3/5 lg:w-1/3 aspect-[6/1] mb-4'>
          <div className='flex flex-row justify-center w-full h-full gap-1'>
            {this.generateColorButtons()}
          </div>
        </div>

        {/* Submit/Clear buttons   */}
        {!disabled && 
        <div className='flex flex-row items-center justify-center h-[20%] w-[60%] lg:w-[40%]'>
          <div className='flex-1 flex flex-row gap-1 h-full aspect-[2/1] justify-center text-white'>
            <button
              className='flex-1 rounded-sm md:rounded-md lg:rounded-lg bg-buttonsBackground' 
              onClick = {() => this.submitCode()} 
              disabled={!this.validateCode()}
              style={{color: !this.validateCode() ? 'red' : ''}}> 
              { !this.validateCode() && 'CAN\'T ' }SUBMIT
            </button>
            <button 
              onClick={() => this.clear()}
              className='flex-1 rounded-sm md:rounded-md lg:rounded-lg bg-buttonsBackground'> 
              CLEAR 
            </button>
          </div>
        </div>
        }

        {/* Drawing Representation 
        *   |                   |
        *   |     |R|G|B|Y|     |
        *   |   |R|G|B|Y|W|B|   |
        *   |   |SUBMIT|CLEAR|  |
        *   |                   |
        */}
      
      </div>
    )
  }
}

interface SelectableInputNodeProps{
  color: string;
  selected: boolean;
  index: number;
  disabled?: boolean;
  callBack: (x: number) => void;
}

class SelectableInputNode extends React.Component<SelectableInputNodeProps>{
  fireCallback(): void{
    this.props.callBack(this.props.index);
  }

  render(): React.ReactNode{
    const { color, selected, disabled } = this.props;
    const style = {
      backgroundColor: color === 'Gray' ? 'transparent' : color,
      border: color === 'Gray' ? `2px dashed` : `0px solid`,
    }
    return (
      <>
        { selected && !disabled ? 
            <div className='h-full p-1 border-2 border-solid aspect-square border-cyan-300'>
              <button className='w-full h-full rounded-sm border-barBackground' style={style} onClick = {() => this.fireCallback()} disabled={disabled}></button>
            </div>
            :
            <button className='h-full rounded-sm aspect-square border-barBackground' style={style} onClick = {() => this.fireCallback()} disabled={disabled}></button>
        }
      </>
    );
  }
}
